/**
 * Server-side capabilities the frontend can rely on. Each flag stands for one
 * task in docs/BACKEND_TASKS.md; while it is off, the frontend keeps its
 * temporary workaround (src/api/listing/legacyCompat.ts and friends). Turn a
 * flag on in the environment once its backend task is deployed, check the
 * site, then delete the workaround it replaces.
 *
 *   VITE_BACKEND_BISHKEK_MIGRATED=true   B1  region CHUI+bishkek → BISHKEK in the DB
 *   VITE_BACKEND_COORDS=true             B2  latitude/longitude filled for every listing
 *   VITE_BACKEND_PRICE_CURRENCY=true     B3  price filters accept priceCurrency=KGS|USD
 *   VITE_BACKEND_RATES=true              B4  GET /api/rates/usd-kgs
 *   VITE_BACKEND_SIMILAR=true            B5  GET /api/property/:id/similar
 *   VITE_BACKEND_AI=true                 B7  POST /api/property/description/generate, POST /api/ai/translate
 *                                        (+ B17 chat messages by id)
 *   VITE_BACKEND_WATERMARK=true          B8  photos watermarked on the server (after the backfill) —
 *                                            the CSS «Turaq.kg» label over photos goes away
 *
 * No flag needed (they degrade quietly on an older server): blocks (B11),
 * conversation reports, moderation error codes (B10), private contacts (B13).
 * Chat v3 (B14–B16) is VITE_CHAT_V3, web push (B18) VITE_WEB_PUSH_PUBLIC_KEY.
 */
const on = (value: string | undefined) => value === "true";
/** Deployed and checked on api.turaq.kg (2026-10-02): on unless the env says "false". */
const onByDefault = (value: string | undefined) => value !== "false";

export const backendFeatures = {
  bishkekMigrated: on(import.meta.env.VITE_BACKEND_BISHKEK_MIGRATED),
  coords: on(import.meta.env.VITE_BACKEND_COORDS),
  priceCurrency: onByDefault(import.meta.env.VITE_BACKEND_PRICE_CURRENCY),
  rates: onByDefault(import.meta.env.VITE_BACKEND_RATES),
  similar: onByDefault(import.meta.env.VITE_BACKEND_SIMILAR),
  ai: onByDefault(import.meta.env.VITE_BACKEND_AI),
  watermark: on(import.meta.env.VITE_BACKEND_WATERMARK),
} as const;

/** API origin (e.g. https://api.turaq.kg) for endpoints that have no VITE_*_URL of their own. */
export function apiUrl(path: string): string {
  const base = import.meta.env.VITE_API_BASE_URL || "";
  try {
    return `${new URL(base).origin}${path}`;
  } catch {
    return path;
  }
}
